'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useScrollManager } from '../contexts/ScrollManagerContext';
import { useScreenLayout } from '../screen/useScreenLayout';

interface UseScrollRestorationOptions {
  scrollKey: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  enabled?: boolean;
}

export const useScrollRestoration = ({ scrollKey, scrollRef, enabled = true }: UseScrollRestorationOptions) => {
  const { saveScroll, getScroll } = useScrollManager();
  const { layoutReady, isPreview } = useScreenLayout();
  const scrollRestoredRef = useRef(false);

  // Wait for layout to be ready before restoring scroll.
  // useLayoutEffect runs while ScreenLayoutProvider's state may still hold
  // the previous screen's chrome heights — sibling setStates from this same
  // commit (ScreenFixedTop, ScreenTabBar register) batch up and flush after
  // we return. queueMicrotask defers the actual scrollTop write to after
  // those flushes but before the next paint, so the restore lands on top
  // of the new screen's settled padding and the user doesn't see the
  // padding-grew-after-restore jerk.
  useLayoutEffect(() => {
    if (!enabled || !scrollRef.current || !layoutReady) return;
    if (scrollRestoredRef.current) return;

    scrollRestoredRef.current = true;
    queueMicrotask(() => {
      if (!scrollRef.current) return;
      const savedPosition = getScroll(scrollKey);
      if (savedPosition > 0) {
        scrollRef.current.scrollTop = savedPosition;
      }
    });
  }, [enabled, scrollKey, scrollRef, getScroll, layoutReady]);

  // Only save scroll in non-preview mode
  // Preview is discarded after swipe, saving there could overwrite the real position
  useEffect(() => {
    if (!enabled || !scrollRef.current || !scrollRestoredRef.current || isPreview) return;

    const element = scrollRef.current;

    const handleScroll = () => {
      saveScroll(scrollKey, element.scrollTop);
    };

    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, scrollKey, scrollRef, saveScroll, layoutReady, isPreview]);

  useEffect(() => {
    return () => {
      scrollRestoredRef.current = false;
    };
  }, []);
};
